import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "!flex-1" }
const _hoisted_2 = /*#__PURE__*/_createTextVNode(" GENERATE ")

import slugify from 'slugify';
import { computed, reactive, Ref, ref, PropType, onMounted } from 'vue';
import { AxiosError } from 'axios';
import { ElNotification } from 'element-plus';
import { useI18n } from 'vue-i18n';
import { orderBy, uniq } from 'lodash';

import { useGetServiceQuery } from '@/entities/services/hooks';
import {
  useGetInspectionGroupQuery,
  useGetInspectionGroupReportMutation, useGetInspectionGroupUseAnnotationTypes,
} from '@/entities/inspection-group/hooks';
import { useGetDeviceQuery, useGetNestedDevicesQuery } from '@/entities/device/hooks';

import { Device } from '@/entities/device/types';
import { InspectionGroup } from '@/entities/inspection-group/types';

import {
  NkForm,
  NkFormItem,
  NkButton,
  NkGroup,
  NkText,
  NkIcon,
  NkCheckbox, NkOption,
} from '@/core/components';

import AnnotationSeveritySelect
  from '@/entities/annotation-severity/components/inputs/AnnotationSeveritySelect.vue';
import AnnotationTypeSelect
  from '@/entities/annotation-type/components/inputs/AnnotationTypeSelect.vue';
import NkSelect from '@/core/components/overlays/Select/NkSelect.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'InspectionPDFForm',
  props: {
  inspectionGroup: {
    type: Number as PropType<InspectionGroup['id']>,
    required: true,
  },
},
  emits: ['submitted'],
  setup(__props, { emit }) {

const props = __props




const { t } = useI18n();

const form = ref();
const isLoading = ref(false);

const cascaderProps = {
  multiple: true,
};

const rules = {};

const langs = [
  {'id': 'en', 'name': 'EN'},
  {'id': 'fr', 'name': 'FR'}
]

const { mutateAsync: getInspectionGroupReport } = useGetInspectionGroupReportMutation()

const inspectionGroupQueryData: Ref = computed(() => ({ id: props.inspectionGroup }));
const hasInspectionGroupQueryData = computed(() => !!inspectionGroupQueryData.value.id);
const { data: inspectionGroup } = useGetInspectionGroupQuery(inspectionGroupQueryData, {
  enabled: hasInspectionGroupQueryData,
});

const serviceQueryData: Ref = computed(() => ({ id: inspectionGroup.value?.service }));
const hasServiceQueryData = computed(() => !!serviceQueryData.value.id);
const { data: service } = useGetServiceQuery(serviceQueryData, { enabled: hasServiceQueryData });

const isVolta = computed(() => service.value && slugify(service.value.name, { lower: true }).includes('volta'));
const isClarity = computed(() => service.value && slugify(service.value.name, { lower: true }).includes('clarity'));
const enabledAnnex = computed(() => isVolta.value || isClarity.value );
const isCertified = computed(() => service.value && [9,10,12,14,16,18].includes(service.value.id));

const availableLocales = computed(() => {
  if(isCertified.value) {
    return langs.filter(lang => lang.id === 'fr')
  }

  return langs;
})

const fields = reactive({
  severities: [],
  types: [],
  devices: [],
  corporate: false,
  annexes: false,
  lang: isCertified.value ? 'fr' : 'en'
});

const rootDeviceQueryData: Ref = computed(() => ({ id: inspectionGroup.value?.device }));
const hasRootDeviceQueryData = computed(() => !!rootDeviceQueryData.value.id);
const { data: rootDevice } = useGetDeviceQuery(rootDeviceQueryData, { enabled: hasRootDeviceQueryData });

const deviceQueryData: Ref = computed(() => ({ id: inspectionGroup.value?.device }));
const hasDeviceQueryData = computed(() => !!deviceQueryData.value.id);
const { data: nestedDevices } = useGetNestedDevicesQuery(deviceQueryData, {
  initialData: () => [],
  enabled: hasDeviceQueryData,
});


const { data: usedAnnotationTypes } = useGetInspectionGroupUseAnnotationTypes(inspectionGroupQueryData)
const annotationTypes = computed(() => orderBy((usedAnnotationTypes.value || []), ['order', 'name']));

const formatDevice: any = (device: Device | undefined) => {
  if (!device) {
    return false;
  }

  if (device.subDevices.length !== 0) {
    return {
      value: device.id,
      label: device.deviceValues.find(o => o.reference === 'name')?.value || '-',
      children: device.subDevices.map(o => formatDevice(nestedDevices.value?.find(nd => nd.id === o))).filter(v => v),
    };
  }

  if (inspectionGroup.value?.inspectionDevices.includes(device.id)) {
    return {
      value: device.id,
      label: device.deviceValues.find(o => o.reference === 'name')?.value || '-',
      children: [],
    }
  }

  return false;
};

const deviceOptions = computed(() => {
  if (!rootDevice.value) {
    return [];
  }

  return [
    {
      value: rootDevice.value.id,
      label: rootDevice.value.deviceValues.find(o => o.reference === 'name')?.value || '-',
      children: rootDevice.value.subDevices.map(o => formatDevice(nestedDevices.value?.find(nd => nd.id === o))).filter(v => v.children?.length > 0),
    },
  ];
});

const flatten = (array: any) => {
    let result: number[] = [];
    array.forEach(a => {
        result.push(a);
        if (Array.isArray(a.children)) {
            result = result.concat(flatten(a.children));
        }
    });
    return result;
}

const handlePDFdownload = () => {
  if (!isLoading.value) {
    form.value.validator.$validate().then((valid: boolean) => {
      if (valid) {
        isLoading.value = true;

        if ( isCertified.value ) {
          fields.lang = 'fr'
        }

        getInspectionGroupReport({
          ...fields,
          id: props.inspectionGroup,
          devices: fields.devices.length !== 0 ? uniq(fields.devices.flat()) : flatten(deviceOptions.value).map(v => v.value),
        }).then((message) => {
          ElNotification({
            type: 'success',
            title: message,
            position: 'bottom-right',
          });
        }).catch((e: AxiosError) => {
          ElNotification({
            type: 'error',
            title: (e?.response?.data as string) || t('inspection_group.notification.report_generation.error'),
            position: 'bottom-right',
          });
        }).finally(() => {
          isLoading.value = false;
        });
      }
    });
  }
};

return (_ctx: any,_cache: any) => {
  const _component_ElCascader = _resolveComponent("ElCascader")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createBlock(_unref(NkForm), {
    ref_key: "form",
    ref: form,
    fields: fields,
    rules: rules
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(NkGroup), {
        spacing: 2,
        "no-wrap": ""
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createVNode(_unref(NkIcon), { name: "NkInfo" })
          ]),
          _createVNode(_unref(NkText), { type: "semibold-xxs-keep" }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('inspection.form.pdf.filter_infos')), 1)
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      (!_unref(isVolta))
        ? (_openBlock(), _createBlock(_unref(NkFormItem), {
            key: 0,
            prop: "types",
            label: _ctx.$t('misc.type')
          }, {
            default: _withCtx(() => [
              _createVNode(NkSelect, {
                modelValue: fields.type,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((fields.type) = $event)),
                multiple: true
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(annotationTypes), (type) => {
                    return (_openBlock(), _createBlock(_unref(NkOption), {
                      key: type.id,
                      value: type.id,
                      label: type.name
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(type.name), 1)
                      ]),
                      _: 2
                    }, 1032, ["value", "label"]))
                  }), 128))
                ]),
                _: 1
              }, 8, ["modelValue"])
            ]),
            _: 1
          }, 8, ["label"]))
        : _createCommentVNode("", true),
      _createVNode(_unref(NkFormItem), {
        prop: "severities",
        label: _ctx.$t('misc.severity')
      }, {
        default: _withCtx(() => [
          (_unref(inspectionGroup)?.service)
            ? (_openBlock(), _createBlock(AnnotationSeveritySelect, {
                key: 0,
                modelValue: fields.severities,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((fields.severities) = $event)),
                service: _unref(inspectionGroup).service,
                multiple: true
              }, null, 8, ["modelValue", "service"]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["label"]),
      _createVNode(_unref(NkFormItem), {
        prop: "devices",
        label: _ctx.$t('misc.device')
      }, {
        default: _withCtx(() => [
          _createVNode(_component_ElCascader, {
            modelValue: fields.devices,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((fields.devices) = $event)),
            clearable: "",
            "collapse-tags-tooltip": "",
            "show-all-levels": false,
            props: cascaderProps,
            options: _unref(deviceOptions)
          }, null, 8, ["modelValue", "options"])
        ]),
        _: 1
      }, 8, ["label"]),
      _createVNode(_unref(NkFormItem), {
        prop: "lang",
        label: _ctx.$t('misc.lang')
      }, {
        default: _withCtx(() => [
          _createVNode(NkSelect, {
            modelValue: fields.lang,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((fields.lang) = $event))
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(availableLocales), (lang) => {
                return (_openBlock(), _createBlock(_unref(NkOption), {
                  key: lang.id,
                  value: lang.id,
                  label: lang.name
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(lang.name), 1)
                  ]),
                  _: 2
                }, 1032, ["value", "label"]))
              }), 128))
            ]),
            _: 1
          }, 8, ["modelValue"])
        ]),
        _: 1
      }, 8, ["label"]),
      _createVNode(_unref(NkGroup), { position: "apart" }, {
        default: _withCtx(() => [
          _createElementVNode("div", null, [
            _createVNode(_unref(NkFormItem), null, {
              default: _withCtx(() => [
                _createVNode(_unref(NkCheckbox), {
                  modelValue: fields.corporate,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((fields.corporate) = $event))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('misc.corporate')), 1)
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _: 1
            }),
            (!_unref(enabledAnnex))
              ? (_openBlock(), _createBlock(_unref(NkFormItem), { key: 0 }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(NkCheckbox), {
                      modelValue: fields.annexes,
                      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((fields.annexes) = $event)),
                      disabled: _unref(isClarity)
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t('misc.annexes')), 1)
                      ]),
                      _: 1
                    }, 8, ["modelValue", "disabled"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]),
          _withDirectives((_openBlock(), _createBlock(_unref(NkButton), {
            icon: "NkDownload",
            disabled: isLoading.value || _unref(inspectionGroup)?.reportStatus === 'ON_GOING',
            onClick: handlePDFdownload
          }, {
            default: _withCtx(() => [
              _hoisted_2
            ]),
            _: 1
          }, 8, ["disabled"])), [
            [_directive_loading, isLoading.value]
          ])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["fields"]))
}
}

})